import NeptuneElement from '../../NeptuneElement.js';
import { i18nMixin } from '../../../utils/i18nMixin.js';
import { i18n } from '../../../utils/i18n.js';

class Header extends i18nMixin(NeptuneElement) {
    templateMarkup = `
        <header>
            <button id="hamburgerButton" type="button" aria-label="Open navigation menu">
                <i class="fa-regular fa-bars" aria-hidden="true"></i>
            </button>
            <div>
                <slot name="logo">
                    <a href="https://www.ecolab.com/" aria-label="Visit Ecolab">
                        <img src="/assets/ecolab-logo/ecolab-logo.svg" alt="Company Logo">
                    </a>
                </slot>
                <span aria-hidden="true">|</span>
                <slot name="application-name"></slot>
            </div>
            <nav id="headerActionNav" data-has-notifications="false" data-has-apps="false" data-has-profile="false">
                <neptune-header-action-item id="notificationActionItem" data-type="nav-item-notifications">
                    <button slot="header-items" type="button" aria-label="Open notifications">
                    <i class="fa-regular fa-bell" aria-hidden="true"></i>
                    </button>
                </neptune-header-action-item>
                <neptune-header-action-item id="appsActionItem" data-type="nav-item-apps">
                    <button slot="header-items" type="button" aria-label="Open apps">
                    <i class="fa-solid fa-grid" aria-hidden="true"></i>
                    </button>
                </neptune-header-action-item>
                <button id="profileActionItem" type="button" aria-label="Open profile menu">
                    <neptune-avatar id="profileAvatar" size="md"></neptune-avatar>
                </button>
                <slot name="eva">
                    <button id="evaButton" type="button" aria-label="Open EVA assistant" hidden>
                        <img src="/assets/eva-logo/eva.png" alt="EVA" />
                    </button>
                </slot>           
            </nav>
            <button id="overflowButton" type="button" aria-label="Open overflow menu">
                <i class="fa-regular fa-ellipsis-vertical" aria-hidden="true"></i>
            </button>
        </header>
    `;

    componentCssUrl = '/src/components/chrome/header/header.css';

    #evaClickHandler = null;
    #logoClickHandler = null;

    #headerActionItems = { logo: {}, eva: {}, apps: [], notifications: [], profile: {} };

    get headerActionItems() { return this.#headerActionItems; }
    set headerActionItems(value) {
        this.#headerActionItems = value;
        if (this.isRendered) { this.initializeHeader(); }
    }

    constructor() {
        super();
        this.hamburgerButton = null;
        this.hamburgerClickHandler = null;
        this.overflowButton = null;
        this.overflowClickHandler = null;
        this.kebabMenuNavigateHandler = null;
        this.sidenavNavigateHandler = null;
        this.activeNav = null;
        this.isTransitioning = false;
    }

    /**
     * Invoked when the component is added to the DOM.
     * Initializes the header and action items.
     * @returns {Promise<void>} Resolves when initialization is complete.
     */
    async connectedCallback() {
        await super.connectedCallback();
        const culture = this.getAttribute('culture');
		if (culture) { i18n.setLocale(culture); }
        if (this.hasAttribute('header-action-items')) {
            const data = this.#headerActionItems || window.headerActionItems;
            const hasData =
                (data?.logo && Object.keys(data.logo).length > 0) ||
                (data?.eva && Object.keys(data.eva).length > 0) ||
                data?.apps?.length > 0 ||
                data?.notifications?.length > 0 ||
                (data?.profile && Object.keys(data.profile).length > 0);

            if (hasData) {
                this.initializeHeader();
            } else {
                console.warn('neptune-header header-action-items flag set but no data found.');
            }
        } else {
            this.initializeHeader();
        }
        this.#applyTranslations();
    }    
        
    /**
     * Invoked when the component is removed from the DOM.
     * Cleans up all event listeners.
     * @returns {Promise<void>} Resolves when cleanup is complete.
    */
    async disconnectedCallback() {
        await this.removeEventListeners();
        super.disconnectedCallback?.();
    }

    
    /**
     * Returns the list of observed attributes.
     * @returns {string[]} Array of observed attribute names.
     */
    static get observedAttributes() {
        return ['header-action-items', 'show-eva', 'culture'];
    }
    
    /**
     * Handles changes to observed attributes.
     * @param {string} name The attribute name.
     * @param {string|null} oldValue The previous value.
     * @param {string|null} newValue The new value.
     * @returns {void}
     */
    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {return;}

        if (name === 'header-action-items') {
            this.updateHeaderActionItems();
            this.#syncLogoFromConfig();
            this.#syncEvaFromConfig();
        } else if (name === 'show-eva') {
            this.#syncEvaVisibility();
        } else if (name === 'culture' && newValue) {
            i18n.setLocale(newValue);
        }
    }
    
    /**
     * Initializes the header component.
     * Sets up menu data, event handlers, and menu listeners.
     * @returns {void}
     */
    initializeHeader() {
        this.#syncLogoFromConfig();
        this.#syncEvaFromConfig();
        this.#injectProfileMenu();
        this.updateHeaderActionItems();
        this.setupMenuClosingLogic();
        this.setupKebabMenuListener();
        this.setEventListeners();
        this.#preloadKebabMenu();
        this.#syncEvaVisibility();
        this.#setupEvaListener();
        this.#setupLogoListener();
    }

    /**
     * @description Injects the neptune-profile-menu component into the shadow DOM if it doesn't already exist.
     * @returns {void}
     */
    #injectProfileMenu() {
        if (this.shadowRoot.querySelector('neptune-profile-menu')) { return; }
        if (!this.id) {
            this.id = `neptune-header-${Math.random().toString(36).slice(2, 8)}`;
        }
        const menu = document.createElement('neptune-profile-menu');
        menu.setAttribute('for', this.id);
        this.shadowRoot.appendChild(menu);
    }

    /**
     * Updates the header action item configuration.
     * Applies menu data and updates navigation state.
     * @returns {void}
     */
    updateHeaderActionItems() {
        if (!this.isRendered) {return;}
        const nav = this.shadowRoot?.querySelector('#headerActionNav');
        if (!nav) {return;}

        const { apps = [], notifications = [], profile = {} } = this.headerActionItems;
        const profileItems = profile && (profile?.menuItems ?? []);
        const hasProfile = profile && (Object.keys(profile).length > 0 || profileItems.length > 0);

        nav.setAttribute('data-has-notifications', notifications.length > 0 ? 'true' : 'false');
        nav.setAttribute('data-has-apps', apps.length > 0 ? 'true' : 'false');
        nav.setAttribute('data-has-profile', hasProfile ? 'true' : 'false');

        const setItems = (selector, items) => {
            if (items.length > 0)
                {nav.querySelector(selector)?.setAttribute('items', JSON.stringify(items));}
        };

        setItems('#notificationActionItem', notifications);
        setItems('#appsActionItem', apps);

        const profileMenu = this.shadowRoot?.querySelector('neptune-profile-menu');
        if (profileMenu) {
            if (Array.isArray(profileItems) && profileItems.length > 0) {
                profileMenu.setAttribute('items', JSON.stringify(profileItems));
            }
            if (profile?.userInfo && Object.keys(profile.userInfo).length > 0) {
                profileMenu.setAttribute('user-info', JSON.stringify(profile.userInfo));
            }
        }

        this.updateProfileInitials();
        const kebab = document.querySelector('neptune-kebab-menu');
        if (kebab) {this.applyKebabMenuData(kebab, this.buildHeaderMenuData());}
    }
    
    /**
     * Updates the displayed profile initials.
     * @returns {void}
     */
    updateProfileInitials() {
        if (!this.isRendered) {return;}
        const { profile } = this.headerActionItems;
        const initials = (profile?.userInfo?.userInitials) || '';
        const avatar = this.shadowRoot?.querySelector('#profileAvatar');
        if (avatar) {avatar.setAttribute('initials', initials);}
        const kebab = document.querySelector('neptune-kebab-menu');
        if (kebab) {this.applyKebabMenuData(kebab, this.buildHeaderMenuData());}

        const profileMenu = this.shadowRoot?.querySelector('neptune-profile-menu');
        if (profileMenu && profile?.userInfo && Object.keys(profile.userInfo).length > 0) {
            profileMenu.setAttribute('user-info', JSON.stringify(profile.userInfo));
        }
    }
    
    /**
     * Closes all open header action item menus.
     * @returns {void}
    */
    closeAllMenus() {
        const actionItems = this.shadowRoot.querySelectorAll('neptune-header-action-item');
        actionItems.forEach(item => item.closeMenu?.());
    }
    
    /**
     * Configures menu closing behavior between header action items.
     * Ensures only one menu is open at a time.
     * @returns {void}
     */
    setupMenuClosingLogic() {
        if (!this.isRendered) {return;}
        const actionItems = this.shadowRoot?.querySelectorAll('neptune-header-action-item');
        if (!actionItems) {return;}

        actionItems.forEach((item) => {
            item.addEventListener('menu-opened', () => {
                actionItems.forEach(otherItem => {
                    if (otherItem !== item) {
                        otherItem.closeMenu();
                    }
                });
            });
            item.addEventListener('item-select', (e) => {
                this.dispatchEvent(new CustomEvent('header-item-selected', {
                    bubbles: true,
                    composed: true,
                    detail: { item: e.detail.item }
                }));                
                const route = e.detail?.item?.route;
                if (route) {
                    window.dispatchEvent(new CustomEvent('neptune-location-change', {
                        detail: { route }
                    }));
                }
            });
        });
    }
    
    /**
     * Registers navigation event listeners for the kebab menu.
     * @returns {void}
     */
    setupKebabMenuListener() {
        if (this.kebabMenuNavigateHandler) {
            const existing = document.querySelector('neptune-kebab-menu');
            if (existing) {
                existing.removeEventListener('neptune-kebab-menu-navigate', this.kebabMenuNavigateHandler);
            }
        }

        const kebab = document.querySelector('neptune-kebab-menu');
        if (!kebab) {return;}

        this.kebabMenuNavigateHandler = e => {
            this.activeNav = null;
            this.dispatchEvent(new CustomEvent('header-item-selected', {
                bubbles: true,
                composed: true,
                detail: {
                    item: {
                        id: e.detail.id,
                        name: e.detail.label,
                        route: e.detail.route
                    }
                }
            }));
        };

        kebab.addEventListener('neptune-kebab-menu-navigate', this.kebabMenuNavigateHandler);
    }
    
    /**
     * Attaches all component event listeners.
     * @returns {Promise<void>} Resolves after listeners are attached.
     */
    setEventListeners() {
        if (!this.isRendered) {return;}

        this.removeEventListeners();

        this.hamburgerButton = this.shadowRoot?.querySelector('#hamburgerButton');
        this.overflowButton = this.shadowRoot?.querySelector('#overflowButton');

        if (this.hamburgerButton) {
            this.hamburgerClickHandler = this.handleHamburgerClick.bind(this);
            this.hamburgerButton.addEventListener('click', this.hamburgerClickHandler);
        }

        if (this.overflowButton) {
            this.overflowClickHandler = this.handleOverflowClick.bind(this);
            this.overflowButton.addEventListener('click', this.overflowClickHandler);
        }

        this.sidenavNavigateHandler = () => { this.activeNav = null; };
        document.addEventListener('neptune-sidenav-navigate', this.sidenavNavigateHandler);
    }
    
    /**
     * Removes all component event listeners.
     * @returns {Promise<void>} Resolves after listeners are removed.
     */
    removeEventListeners() {
        if (this.hamburgerButton && this.hamburgerClickHandler) {
            this.hamburgerButton.removeEventListener('click', this.hamburgerClickHandler);
        }
        this.hamburgerButton = null;
        this.hamburgerClickHandler = null;

        if (this.overflowButton && this.overflowClickHandler) {
            this.overflowButton.removeEventListener('click', this.overflowClickHandler);
        }
        this.overflowButton = null;
        this.overflowClickHandler = null;

        if (this.kebabMenuNavigateHandler) {
            const kebab = document.querySelector('neptune-kebab-menu');
            if (kebab) {
                kebab.removeEventListener('neptune-kebab-menu-navigate', this.kebabMenuNavigateHandler);
            }
            this.kebabMenuNavigateHandler = null;
        }

        if (this.sidenavNavigateHandler) {
            document.removeEventListener('neptune-sidenav-navigate', this.sidenavNavigateHandler);
            this.sidenavNavigateHandler = null;
        }
        this.#removeEvaListener();        
        this.#removeLogoListener();
    }
    
    /**
     * Handles hamburger menu interactions.
     * Toggles the sidenav state and emits navigation events.
     * @returns {void}
     */
    handleHamburgerClick() {
        if (this.isTransitioning) {return;}
        this.isTransitioning = true;

        this.closeAllMenus();
        this.#forceCloseKebab();

        const sidenav = this.ensureSidenavExists();
        const willOpen = this.activeNav !== 'left';

        sidenav.setAttribute('state', willOpen ? 'primary-drawer' : 'rail');
        this.activeNav = willOpen ? 'left' : null;

        this.emitHamburgerToggle({
            isOpen: willOpen,
            navState: willOpen ? 'primary-drawer' : 'rail'
        });

        setTimeout(() => { this.isTransitioning = false; }, 300);
    }
    
    /**
     * Handles overflow menu interactions.
     * Toggles the kebab menu state and emits navigation events.
     * @returns {void}
     */
    handleOverflowClick() {
        if (this.isTransitioning) {return;}
        this.isTransitioning = true;

        this.closeAllMenus();
        this.#forceCloseSidenav();

        const kebab = this.ensureKebabMenuExists();
        this.setupKebabMenuListener();

        const willOpen = this.activeNav !== 'right';

        if (willOpen) {
            this.applyKebabMenuData(kebab, this.buildHeaderMenuData());
            kebab.open();
            this.activeNav = 'right';
            this.emitOverflowToggle({ isOpen: true, navState: 'primary-drawer' });
        } else {
            kebab.close();
            this.activeNav = null;
            this.emitOverflowToggle({ isOpen: false, navState: '' });
        }

        setTimeout(() => { this.isTransitioning = false; }, 300);
    }
    
    /**
     * Forces the kebab menu to close.
     * Resets active navigation state when required.
     * @returns {void}
     */
    #forceCloseKebab() {
        const kebab = document.querySelector('neptune-kebab-menu');
        if (!kebab) {return;}
        if (kebab.hasAttribute('state')) {kebab.removeAttribute('state');}
        if (this.activeNav === 'right') {this.activeNav = null;}
    }
    
    /**
     * Forces the sidenav to close.
     * Resets active navigation state when required.
     * @returns {void}
     */
    #forceCloseSidenav() {
        const sidenav = document.querySelector('neptune-sidenav');
        if (!sidenav) {return;}
        if ((sidenav.getAttribute('state') || 'rail') !== 'rail') {
            sidenav.setAttribute('state', 'rail');
        }
        if (this.activeNav === 'left') {this.activeNav = null;}
    }
    
    /**
     * Ensures a kebab menu element exists in the document.
     * Creates one if it does not exist.
     * @returns {HTMLElement} The kebab menu element.
     */
    ensureKebabMenuExists() {
        let kebab = document.querySelector('neptune-kebab-menu');
        if (!kebab) {
            kebab = document.createElement('neptune-kebab-menu');
            document.body.appendChild(kebab);
        }
        return kebab;
    }
    
    /**
     * Ensures a sidenav element exists in the document.
     * Creates one if it does not exist.
     * @returns {HTMLElement} The sidenav element.
     */
    ensureSidenavExists() {
        let sidenav = document.querySelector('neptune-sidenav');
        if (!sidenav) {
            sidenav = document.createElement('neptune-sidenav');
            sidenav.setAttribute('state', 'rail');
            document.body.appendChild(sidenav);
        }
        return sidenav;
    }
    
    /**
     * Builds menu data from configured header action items.
     * @returns {Array<Object>} Formatted menu data for the kebab menu.
     */
    buildHeaderMenuData() {
        const { apps = [], notifications = [], profile = {} } = this.headerActionItems;
        const profileItems = profile && (profile?.menuItems ?? []);
        const profileInfo = profile && Object.keys(profile).length > 0 && profile?.userInfo ? profile.userInfo : {};
        const initials = profileInfo?.userInitials || '';
        const toChild = item => ({
            id: item.id,
            label: item.name || item.title || '',
            route: item.route
        });

        const sections = [];
        if (apps.length > 0)
            {sections.push({ label: this.translate('HEADER.LABEL.APPS'), icon: 'fa-solid fa-grid', route: apps[0].route, subMenu: apps.map(toChild) });}

        if (notifications.length > 0)
            {sections.push({ label: this.translate('HEADER.LABEL.NOTIFICATIONS'), icon: 'fa-regular fa-bell', route: notifications[0].route, subMenu: notifications.map(toChild) });}

        if (profileItems.length > 0)
            {sections.push({ label: profileInfo?.userName || this.translate('HEADER.LABEL.PROFILE'), avatarText: initials, route: profileItems[0].route, subMenu: profileItems.map(toChild) });}

        return sections;
    }
    
    /**
     * Applies menu data to the kebab menu component.
     * @param {HTMLElement} kebab The kebab menu element.
     * @param {Array<Object>} menuData The menu items to display.
     * @returns {void}
     */
    applyKebabMenuData(kebab, menuData) {
        const apply = () => {
            if (typeof kebab.setKebabItems === 'function') {
                kebab.setKebabItems(menuData);
            } else {
                kebab.pendingKebabItems = menuData;
            }
        };

        customElements.get('neptune-kebab-menu')
            ? apply()
            : customElements.whenDefined('neptune-kebab-menu').then(apply);
    }

    /**
     * Preloads the kebab menu and its data.
     * @returns {void}
     */
    #preloadKebabMenu() {
        const kebab = this.ensureKebabMenuExists();
        this.setupKebabMenuListener();
        this.applyKebabMenuData(kebab, this.buildHeaderMenuData());
    }    

    /**
     * Toggles EVA button visibility based on the show-eva attribute.
     * @returns {void}
     */
    #syncEvaVisibility() {
        const evaBtn = this.shadowRoot?.querySelector('#evaButton');
        if (!evaBtn) {return;}
        const shouldShow = this.hasAttribute('show-eva');
        if (shouldShow) {evaBtn.removeAttribute('hidden');}
        else {evaBtn.setAttribute('hidden', '');}
    }

    /**
     * Attaches click listener to the default EVA button to dispatch eva-click.
     * @returns {void}
     */
    #setupEvaListener() {
        const evaBtn = this.shadowRoot?.querySelector('#evaButton');
        if (!evaBtn) {return;}
        this.#evaClickHandler = () => {
            this.dispatchEvent(new CustomEvent('eva-click', { 
                bubbles: true,composed: true,
             }));
        };
        evaBtn.addEventListener('click', this.#evaClickHandler);
    }

    /**
     * Removes EVA click listener during cleanup.
     * @returns {void}
     */
    #removeEvaListener() {
        const evaBtn = this.shadowRoot?.querySelector('#evaButton');
        if (evaBtn && this.#evaClickHandler) {
            evaBtn.removeEventListener('click', this.#evaClickHandler);
        }
        this.#evaClickHandler = null;
    }

    /**
     * Attaches click listener on the default logo to dispatch logo-click.
     * @returns {void}
     */
    #setupLogoListener() {
        const logoSlot = this.shadowRoot?.querySelector('slot[name="logo"]');
        if (!logoSlot || (logoSlot.assignedNodes() || []).length > 0) {return;}
        const link = logoSlot.querySelector('a');
        if (!link) {return;}
        this.#logoClickHandler = (event) => {
            const logoConfig = this.headerActionItems?.logo || {};
            const isCustomLogo = Object.keys(logoConfig).length > 0;

            if (isCustomLogo) {
                event.preventDefault();
                this.dispatchEvent(new CustomEvent('logo-click', {
                    bubbles: true, composed: true, cancelable: true,
                    detail: this.headerActionItems?.logo || {}
                }));
            }            
        };
        link.addEventListener('click', this.#logoClickHandler);
    }

    /**
     * Removes logo click listener during cleanup.
     * @returns {void}
     */
    #removeLogoListener() {
        const link = this.shadowRoot?.querySelector('slot[name="logo"] > a');
        if (link && this.#logoClickHandler) {
            link.removeEventListener('click', this.#logoClickHandler);
        }
        this.#logoClickHandler = null;
    }

    /**
     * Applies logo config (path, route, alt) to the default logo when
     * the consumer hasn't provided a slotted logo. Falls back to defaults.
     * @returns {void}
     */
    #syncLogoFromConfig() {
        const logoSlot = this.shadowRoot?.querySelector('slot[name="logo"]');
        if (!logoSlot || (logoSlot.assignedNodes() || []).length > 0) {return;}
        const link = logoSlot.querySelector('a');
        const img = link?.querySelector('img');
        if (!link || !img) {return;}
        const logoConfig = this.headerActionItems?.logo || {};
        img.src = logoConfig.path || '/assets/ecolab-logo/ecolab-logo.svg';
        img.alt = logoConfig.alt || 'Company Logo';
        link.href = 'https://www.ecolab.com/';
        link.setAttribute('aria-label', `Visit ${logoConfig.alt || 'Ecolab'}`);
    }

    /**
     * Applies EVA config (path, alt) to the default EVA button when
     * the consumer hasn't provided a slotted EVA. Falls back to defaults.
     * @returns {void}
     */
    #syncEvaFromConfig() {
        const evaSlot = this.shadowRoot?.querySelector('slot[name="eva"]');
        if (!evaSlot || (evaSlot.assignedNodes() || []).length > 0) {return;}
        const btn = this.shadowRoot?.querySelector('#evaButton');
        const img = btn?.querySelector('img');
        if (!btn || !img) {return;}
        const evaConfig = this.headerActionItems?.eva || {};
        img.src = evaConfig.path || '/assets/eva-logo/eva.png';
        img.alt = evaConfig.alt || 'EVA';
        btn.setAttribute('aria-label', evaConfig.alt ? `Open ${evaConfig.alt}` : 'Open EVA assistant');
    }
    
    /**
     * Emits a hamburger toggle event.
     * @param {Object} options Toggle options.
     * @param {boolean} options.isOpen Indicates whether the sidenav is open.
     * @param {string} options.navState The current navigation state.
     * @returns {void}
     */
    emitHamburgerToggle({ isOpen, navState }) {
        this.dispatchEvent(
            new CustomEvent('hamburger-toggle', {
                bubbles: true,
                composed: true,
                detail: { isOpen, navState }
            })
        );
    }

    /**
     * Emits an overflow toggle event.
     * @param {Object} options Toggle options.
     * @param {boolean} options.isOpen Indicates whether the overflow menu is open.
     * @param {string} options.navState The current navigation state.
     * @returns {void}
    */
    emitOverflowToggle({ isOpen, navState }) {
        this.dispatchEvent(
            new CustomEvent('overflow-toggle', {
                bubbles: true,
                composed: true,
                detail: { isOpen, navState }
            })
        );
    }

     /**
     * Updates the kebab menu labels when the locale changes.
     * Rebuilds menu data using translated HEADER.LABEL values.
     * @returns {void}
     */
    onLocaleChange() {
        this.#applyTranslations();

        const kebab = document.querySelector('neptune-kebab-menu');
        if (kebab) {
            this.applyKebabMenuData(kebab, this.buildHeaderMenuData());
        }
    }

    /**
     * Applies translations to the header component based on the current locale.
     * Updates aria-labels on all interactive elements.
     * @returns {void}
     */
    #applyTranslations() {
        if (!this.isRendered) { return; }

        const hamburgerButton = this.shadowRoot?.querySelector('#hamburgerButton');
        const notificationButton = this.shadowRoot?.querySelector('#notificationActionItem button');
        const appsButton = this.shadowRoot?.querySelector('#appsActionItem button');
        const profileButton = this.shadowRoot?.querySelector('#profileActionItem');
        const evaButton = this.shadowRoot?.querySelector('#evaButton');
        const overflowButton = this.shadowRoot?.querySelector('#overflowButton');
        const logoLink = this.shadowRoot?.querySelector('slot[name="logo"] > a');
        const logoImg = this.shadowRoot?.querySelector('slot[name="logo"] > a > img');

        if (hamburgerButton) {
            hamburgerButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_NAVIGATION_MENU'));
        }
        if (notificationButton) {
            notificationButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_NOTIFICATIONS'));
        }
        if (appsButton) {
            appsButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_APPS'));
        }
        if (profileButton) {
            profileButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_PROFILE_MENU'));
        }
        if (evaButton) {
            evaButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_EVA'));
        }
        if (overflowButton) {
            overflowButton.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.OPEN_OVERFLOW_MENU'));
        }
        if (logoImg) {
            logoImg.setAttribute('alt', this.translate('HEADER.ARIA_LABEL.COMPANY_LOGO'));
        }
        if (logoLink) {
            logoLink.setAttribute('aria-label', this.translate('HEADER.ARIA_LABEL.VISIT_ECOLAB'));
        }
    }
}

customElements.define('neptune-header', Header);
export default Header;
